import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Link,
  Alert,
  Paper,
} from '@mui/material';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signIn(email, password);
      navigate('/dashboard');
    } catch (error) {
      setError('Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a' }}>
      {/* Header */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderBottom: 1,
          borderColor: '#1a1a1a',
          backdropFilter: 'blur(8px)',
          bgcolor: 'rgba(10, 10, 10, 0.9)'
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 2
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
              onClick={() => navigate('/')}
            >
              <img
                src="/praytell_logo.png"
                alt="mycatalyze"
                style={{ height: 32, width: 'auto', filter: 'brightness(0) invert(1)' }}
              />
              <Typography variant="h6" component="h1" fontWeight="bold" sx={{ color: 'white' }}>
                MyCatalyze
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
      
      <Container maxWidth="sm">
        <Box sx={{ mt: 12, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper sx={{ 
            p: 4, 
            width: '100%', 
            bgcolor: '#2a2a2a',
            border: '1px solid #3a3a3a',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <Typography component="h1" variant="h5" align="center" gutterBottom sx={{ color: 'white' }}>
              Sign In
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#4a4a4a',
                    },
                    '&:hover fieldset': {
                      borderColor: '#5a5a5a',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6a6a6a',
                    },
                    '& input': {
                      color: 'white',
                    },
                    '& label': {
                      color: '#b0b0b0',
                    },
                    '&.Mui-focused label': {
                      color: '#6a6a6a',
                    },
                  },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#4a4a4a',
                    },
                    '&:hover fieldset': {
                      borderColor: '#5a5a5a',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6a6a6a',
                    },
                    '& input': {
                      color: 'white',
                    },
                    '& label': {
                      color: '#b0b0b0',
                    },
                    '&.Mui-focused label': {
                      color: '#6a6a6a',
                    },
                  },
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  bgcolor: '#3a3a3a',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#4a4a4a',
                  },
                  '&:disabled': {
                    bgcolor: '#2a2a2a',
                    color: '#666',
                  }
                }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/register')}
                  sx={{
                    color: '#5a5a5a',
                    '&:hover': {
                      color: '#7a7a7a',
                    }
                  }}
                >
                  Don't have an account? Sign Up
                </Link>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
} 